import type { IconList } from '@snowit/icetypes';
import type {
  CardStatusEnum,
  CheckoutCodeStatusEnum,
  DealerOrderStatusEnum,
  ExternalCodeStatusEnum,
  FormStatusEnum,
  MembershipCardAssignmentStatusEnum,
  MembershipDiscountStatusEnum,
  MembershipStatusEnum,
  MembershipTransitStatusEnum,
  NotificationStatusEnum,
  OrderStatusEnum,
  PaymentStatusEnum,
  ProfileStatusEnum,
  ProviderOrderMiddlewareResponseEnum,
  ProviderOrderStatusEnum,
  ProviderOrderStatusSemaphoreEnum,
  TaskStatusEnum,
} from '@snowit/models';
import type { ValueOf } from '@snowit/types/shared';

type IceStatusTheme =
  | 'default'
  | 'ok'
  | 'warning'
  | 'error'
  | 'cancel'
  | 'warning2'
  | 'info'
  | 'unknown';
export const ICE_STATUS_LIST = [
  'great',
  'great_wait',
  'good',
  'good_wait',
  'warn',
  'warn_wait',
  'process',
  'process_wait',
  'error',
  'error_wait',
  'cancel',
  'cancel_wait',
  'expired',
  'disabled',
  'on_hold',
  'unknown',
] as const;

const IceStatusIcon = [
  'hexagon',
  'hexagon_2',
  'circle',
  'circle_2',
  'triangle',
  'triangle_2',
  'triangle_3',
  'triangle_4',
  'square',
  'square_2',
  'square_3',
  'square_4',
  'disabled',
] as const satisfies ReadonlyArray<IconList>;

export const ICE_STATUS: Record<
  (typeof ICE_STATUS_LIST)[number],
  [(typeof IceStatusIcon)[number], IceStatusTheme]
> = {
  great: ['hexagon', 'ok'],
  great_wait: ['hexagon_2', 'ok'],
  good: ['circle', 'ok'],
  good_wait: ['circle_2', 'ok'],
  warn: ['triangle', 'warning'],
  warn_wait: ['triangle_2', 'warning'],
  process: ['triangle_3', 'warning2'],
  process_wait: ['triangle_4', 'warning'],
  error: ['square', 'error'],
  error_wait: ['square_2', 'error'],
  cancel: ['square_3', 'cancel'],
  cancel_wait: ['square_4', 'cancel'],
  expired: ['square', 'unknown'],
  disabled: ['disabled', 'default'],
  on_hold: ['hexagon', 'info'],
  unknown: ['circle_2', 'default'],
};

type Status = ValueOf<typeof ICE_STATUS>;
export const ITEM_TRANSFER_STATUS = [
  'fulfilled',
  'refunded',
  'pending',
  'processing',
  'error',
] as const;

export type ItemTransferStatusEnum = (typeof ITEM_TRANSFER_STATUS)[number];
export const SYNC_STATUS = ['fulfilled', 'processing', 'pending'] as const;
export type SyncStatusEnum = (typeof SYNC_STATUS)[number];
export const STAGE_STATUS = ['active', 'not_active'] as const;
export type StageStatusEnum = (typeof STAGE_STATUS)[number];
export const EMAIL_STATUS = ['enabled', 'disabled'] as const;
export type EmailStatusEnum = (typeof EMAIL_STATUS)[number];
export const TENANT_STATUS = ['open', 'closed'] as const;
export type TenantStatusEnum = (typeof TENANT_STATUS)[number];
export const BASIC_STATUS = ['true', 'false'] as const;
export type BasicStatusEnum = (typeof BASIC_STATUS)[number];
export const IMPORT_STATUS = ['elaborated', 'processing', 'assigned-to-task', 'running'] as const;
export type ImportStatusEnum = (typeof IMPORT_STATUS)[number];

export const MEMBERSHIP_PAYMENT_STATUS = [
  'canceled',
  'processing',
  'requires_action',
  'requires_capture',
  'requires_confirmation',
  'requires_payment_method',
  'succeeded',
] as const;
export type MembershipPaymentStatusEnum = (typeof MEMBERSHIP_PAYMENT_STATUS)[number];

export type StatusObject = {
  order: Record<OrderStatusEnum, Status>;
  membership: Record<MembershipStatusEnum, Status>;
  membership_card: Record<MembershipCardAssignmentStatusEnum, Status>;
  notification: Record<NotificationStatusEnum, Status>;
  membership_transit: Record<MembershipTransitStatusEnum, Status>;
  membership_payment: Record<MembershipPaymentStatusEnum, Status>;
  membership_discount: Record<MembershipDiscountStatusEnum, Status>;
  item_transfer: Record<ItemTransferStatusEnum, Status>;
  dealer_order: Record<DealerOrderStatusEnum, Status>;
  task: Record<TaskStatusEnum, Status>;
  payment: Record<PaymentStatusEnum, Status>;
  provider_order: Record<ProviderOrderStatusEnum, Status>;
  provider_semaphore: Record<ProviderOrderStatusSemaphoreEnum, Status>;
  sync_status: Record<SyncStatusEnum, Status>;
  provider_middleware: Record<ProviderOrderMiddlewareResponseEnum, Status>;
  skipass_promos: Record<FormStatusEnum, Status>;
  checkout_codes: Record<CheckoutCodeStatusEnum, Status>;
  external_codes: Record<ExternalCodeStatusEnum, Status>;
  snowitcard: Record<CardStatusEnum, Status>;
  stage: Record<StageStatusEnum, Status>;
  import: Record<ImportStatusEnum, Status>;
  email: Record<EmailStatusEnum, Status>;
  tenant: Record<TenantStatusEnum, Status>;
  profile: Record<ProfileStatusEnum, Status>;
  basic: Record<BasicStatusEnum, Status>;
};

export const STATUSES: StatusObject = {
  order: {
    'on-hold': ICE_STATUS.great_wait,
    fulfilled: ICE_STATUS.great,
    ok: ICE_STATUS.good,
    pending: ICE_STATUS.warn_wait,
    processing: ICE_STATUS.process,
    expired: ICE_STATUS.expired,
    canceled: ICE_STATUS.cancel,
  },
  membership: {
    // pending: ICE_STATUS.warn_wait,
    pending: ICE_STATUS.warn_wait,
    active: ICE_STATUS.good,
    canceled: ICE_STATUS.cancel,
    unpaid: ICE_STATUS.cancel,
    incomplete: ICE_STATUS.warn_wait,
    incomplete_expired: ICE_STATUS.error,
    past_due: ICE_STATUS.warn_wait,
    paused: ICE_STATUS.warn_wait,
    trialing: ICE_STATUS.great,
  },
  membership_card: {
    assigned: ICE_STATUS.good,
    blocked: ICE_STATUS.cancel,
    error: ICE_STATUS.error,
    pending: ICE_STATUS.warn,
  },
  membership_payment: {
    canceled: ICE_STATUS.cancel,
    processing: ICE_STATUS.process,
    requires_action: ICE_STATUS.process,
    requires_capture: ICE_STATUS.process,
    requires_confirmation: ICE_STATUS.process,
    requires_payment_method: ICE_STATUS.process,
    succeeded: ICE_STATUS.great,
  },
  dealer_order: {
    delivered: ICE_STATUS.great,
    pending: ICE_STATUS.good_wait,
    canceled: ICE_STATUS.cancel,
    cash: ICE_STATUS.unknown,
  },
  task: {
    fulfilled: ICE_STATUS.great,
    pending: ICE_STATUS.warn_wait,
    running: ICE_STATUS.process,
    error: ICE_STATUS.error,
  },
  payment: {
    failed: ICE_STATUS.unknown,
    paid: ICE_STATUS.unknown,
    pending: ICE_STATUS.unknown,
    processing: ICE_STATUS.unknown,
    refunded: ICE_STATUS.unknown,
  },
  provider_order: {
    ok: ICE_STATUS.good,
    needs_confirmation: ICE_STATUS.good_wait,
    delayed: ICE_STATUS.warn,
    pending: ICE_STATUS.warn_wait,
    waiting: ICE_STATUS.warn_wait,
    pending_preorder: ICE_STATUS.process_wait,
    failed: ICE_STATUS.error,
    cancelled: ICE_STATUS.cancel,
    cancelling: ICE_STATUS.cancel_wait,
    blocked: ICE_STATUS.error_wait,
  },
  provider_semaphore: {
    green: ICE_STATUS.good,
    yellow: ICE_STATUS.good_wait,
    red: ICE_STATUS.cancel,
  },
  provider_middleware: {
    batch: ICE_STATUS.unknown,
    cancelled: ICE_STATUS.unknown,
    needs_confirmation: ICE_STATUS.unknown,
    ok: ICE_STATUS.unknown,
    pending: ICE_STATUS.unknown,
  },
  skipass_promos: {
    redeemed: ICE_STATUS.great,
    sent: ICE_STATUS.good,
    pending: ICE_STATUS.warn_wait,
    error: ICE_STATUS.error,
    disabled: ICE_STATUS.error,
  },
  checkout_codes: {
    pending: ICE_STATUS.warn_wait,
    redeemed: ICE_STATUS.great,
  },
  external_codes: {
    created: ICE_STATUS.great,
    sent: ICE_STATUS.warn_wait,
    used: ICE_STATUS.cancel,
    canceled: ICE_STATUS.error,
  },
  snowitcard: {
    valid: ICE_STATUS.good,
    pending: ICE_STATUS.warn_wait,
    warning: ICE_STATUS.warn,
    rejected: ICE_STATUS.error,
    'not-assigned': ICE_STATUS.disabled,
    membership: ICE_STATUS.great_wait,
  },
  stage: {
    active: ICE_STATUS.good,
    not_active: ICE_STATUS.error,
  },
  import: {
    elaborated: ICE_STATUS.good,
    processing: ICE_STATUS.warn_wait,
    running: ICE_STATUS.process,
    'assigned-to-task': ICE_STATUS.warn,
  },
  email: {
    enabled: ICE_STATUS.good,
    disabled: ICE_STATUS.disabled,
  },
  tenant: {
    open: ICE_STATUS.good,
    closed: ICE_STATUS.disabled,
  },
  basic: {
    true: ICE_STATUS.good,
    false: ICE_STATUS.warn,
  },
  notification: {
    error: ICE_STATUS.error,
    pending: ICE_STATUS.warn,
    sent: ICE_STATUS.great,
  },
  membership_transit: {
    closed: ICE_STATUS.good,
    pending: ICE_STATUS.warn,
    discarded: ICE_STATUS.cancel,
  },
  item_transfer: {
    fulfilled: ICE_STATUS.great,
    refunded: ICE_STATUS.good,
    pending: ICE_STATUS.warn_wait,
    processing: ICE_STATUS.process,
    error: ICE_STATUS.error,
  },
  sync_status: {
    fulfilled: ICE_STATUS.good,
    processing: ICE_STATUS.process,
    pending: ICE_STATUS.good_wait,
  },
  profile: {
    warning: ICE_STATUS.warn,
    rejected: ICE_STATUS.error,
    valid: ICE_STATUS.good,
    'not-verified': ICE_STATUS.warn_wait,
  },
  membership_discount: {
    redeemed: ICE_STATUS.good,
    checked: ICE_STATUS.good_wait,
    error: ICE_STATUS.error,
  },
};
